import apiService, { ServiceType } from './api';

/**
 * Client for the technical-support endpoints (BE `/support/*`).
 *
 * Messages with attachments go up as multipart; the shared axios instance defaults to JSON so
 * each upload call overrides the content type and lets the browser set the boundary.
 * Attachment `url`s come back relative and signed; `attachmentSrc()` makes them absolute.
 */

export type SupportTicketStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type SupportTicketCategory = 'BUG' | 'QUESTION' | 'FEATURE_REQUEST' | 'ACCOUNT' | 'OTHER';
export type SupportSenderRole = 'USER' | 'ADMIN';
export type SupportAttachmentKind = 'IMAGE' | 'VIDEO';

export interface SupportAttachment {
  id: string;
  kind: SupportAttachmentKind;
  fileName: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderRole: SupportSenderRole;
  senderName: string | null;
  body: string;
  isSystem: boolean;
  createdAt: string;
  attachments: SupportAttachment[];
}

export interface SupportTicketSummary {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  subject: string;
  category: SupportTicketCategory;
  status: SupportTicketStatus;
  assigneeId: string | null;
  lastMessageAt: string;
  lastMessageText: string | null;
  lastSenderRole: SupportSenderRole;
  userReadAt: string;
  adminReadAt: string | null;
  unread: boolean;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  messages: SupportMessage[];
}

export interface SupportUploadLimits {
  maxFilesPerMessage: number;
  maxImageBytes: number;
  maxVideoBytes: number;
  allowedMimeTypes: string[];
}

export const DEFAULT_UPLOAD_LIMITS: SupportUploadLimits = {
  maxFilesPerMessage: 5,
  maxImageBytes: 10 * 1024 * 1024,
  maxVideoBytes: 100 * 1024 * 1024,
  allowedMimeTypes: [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/webm',
    'video/quicktime',
  ],
};

export const CATEGORY_LABEL: Record<SupportTicketCategory, string> = {
  BUG: 'Bug',
  QUESTION: 'Question',
  FEATURE_REQUEST: 'Feature request',
  ACCOUNT: 'Account',
  OTHER: 'Other',
};

export const STATUS_LABEL: Record<SupportTicketStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const core = () => apiService.getService(ServiceType.CORE);

const API_BASE = (process.env.REACT_APP_CORE_SERVICE_URL || 'http://localhost:3000').replace(
  /\/$/,
  '',
);

/** Absolute URL for an attachment's signed relative `url`. */
export function attachmentSrc(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${API_BASE}${url}`;
}

/** Unread from the user's point of view; used to fold socket updates without a refetch. */
export function isUnreadForUser(t: SupportTicketSummary): boolean {
  return t.lastSenderRole === 'ADMIN' && new Date(t.lastMessageAt) > new Date(t.userReadAt);
}

export async function getSupportLimits(): Promise<SupportUploadLimits> {
  const { data } = await core().get<SupportUploadLimits>('/support/limits');
  return data;
}

export async function getSupportUnread(): Promise<number> {
  const { data } = await core().get<{ tickets: number }>('/support/unread');
  return data.tickets;
}

export async function listSupportTickets(
  status?: SupportTicketStatus,
): Promise<SupportTicketSummary[]> {
  const { data } = await core().get<SupportTicketSummary[]>('/support/tickets', {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function getSupportTicket(ticketId: string): Promise<SupportTicketDetail> {
  const { data } = await core().get<SupportTicketDetail>(`/support/tickets/${ticketId}`);
  return data;
}

export interface CreateTicketInput {
  subject: string;
  category: SupportTicketCategory;
  body: string;
  files: File[];
}

export type UploadProgress = (percent: number) => void;

function multipart(fields: Record<string, string | undefined>, files: File[]): FormData {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== '') form.append(k, v);
  });
  files.forEach((f) => form.append('files', f, f.name));
  return form;
}

const uploadConfig = (onProgress?: UploadProgress) => ({
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 10 * 60 * 1000,
  onUploadProgress: (e: { loaded: number; total?: number }) => {
    if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
  },
});

export async function createSupportTicket(
  input: CreateTicketInput,
  onProgress?: UploadProgress,
): Promise<SupportTicketDetail> {
  const form = multipart(
    { subject: input.subject, category: input.category, body: input.body },
    input.files,
  );
  const { data } = await core().post<SupportTicketDetail>(
    '/support/tickets',
    form,
    uploadConfig(onProgress),
  );
  return data;
}

export async function sendSupportMessage(
  ticketId: string,
  body: string,
  files: File[],
  onProgress?: UploadProgress,
): Promise<SupportMessage> {
  const form = multipart({ body }, files);
  const { data } = await core().post<SupportMessage>(
    `/support/tickets/${ticketId}/messages`,
    form,
    uploadConfig(onProgress),
  );
  return data;
}

export async function markSupportTicketRead(ticketId: string): Promise<SupportTicketSummary> {
  const { data } = await core().post<SupportTicketSummary>(`/support/tickets/${ticketId}/read`);
  return data;
}

/**
 * Client-side pre-check so a 100 MB video is refused before it leaves the browser. Returns
 * a message for the first offending file, or null when the batch is fine.
 */
export function validateFiles(
  existing: File[],
  incoming: File[],
  limits: SupportUploadLimits,
): string | null {
  if (existing.length + incoming.length > limits.maxFilesPerMessage) {
    return `You can attach up to ${limits.maxFilesPerMessage} files per message.`;
  }
  for (const f of incoming) {
    if (!limits.allowedMimeTypes.includes(f.type)) {
      return `${f.name}: only images (png, jpg, gif, webp) and videos (mp4, webm, mov) are accepted.`;
    }
    const isVideo = f.type.startsWith('video/');
    const cap = isVideo ? limits.maxVideoBytes : limits.maxImageBytes;
    if (f.size > cap) {
      return `${f.name} is too large. ${isVideo ? 'Videos' : 'Images'} must be under ${Math.round(cap / 1024 / 1024)} MB.`;
    }
  }
  return null;
}
